var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as AccountService from '../service/account-service.js';
import * as BookService from '../service/book-service.js';
import * as QueryService from '../service/query-service.js';
import * as BalancesService from '../service/balances-service.js';
import * as FileService from '../service/file-service.js';
import * as GroupService from '../service/group-service.js';
import * as IntegrationService from '../service/integration-service.js';
import * as TransactionService from '../service/transaction-service.js';
import * as EventService from '../service/event-service.js';
import * as CollaboratorService from '../service/collaborator-service.js';
import * as Utils from '../utils.js';
import { ResourceProperty } from './ResourceProperty.js';
import { Account } from './Account.js';
import { Collaborator } from './Collaborator.js';
import { Collection } from './Collection.js';
import { Permission } from './Enums.js';
import { EventList } from './EventList.js';
import { File } from './File.js';
import { FileList } from './FileList.js';
import { Group } from './Group.js';
import { Integration } from './Integration.js';
import { Transaction } from './Transaction.js';
import { TransactionList } from './TransactionList.js';
import { BalancesReport } from './BalancesReport.js';
import { App } from './App.js';
import { Query } from './Query.js';
import { Bkper } from './Bkper.js';
import { Backlog } from './Backlog.js';
import { AccountsDataTableBuilder } from '../builders/AccountsDataTableBuilder.js';
import { GroupsDataTableBuilder } from '../builders/GroupsDataTableBuilder.js';
import { TransactionsDataTableBuilder } from '../builders/TransactionsDataTableBuilder.js';
/**
 * A Book represents a [General Ledger](https://en.wikipedia.org/wiki/General_ledger) for a company or business, but can also represent a [Ledger](https://en.wikipedia.org/wiki/Ledger) for a project or department
 *
 * It contains all [[Accounts]] where [[Transactions]] are recorded/posted;
 *
 * @public
 */
export class Book extends ResourceProperty {
    constructor(payload, config) {
        super(payload);
        this.allGroupsLoaded = false;
        this.allAccountsLoaded = false;
        this.config = config;
        if (this.payload.permission == Permission.RECORDER) {
            this.payload.groups = this.payload.groups || [];
            this.payload.accounts = this.payload.accounts || [];
        }
        this.allGroupsLoaded = this.mapGroups(this.payload.groups);
        this.allAccountsLoaded = this.mapAccounts(this.payload.accounts);
    }
    /** @internal */
    getConfig() {
        return this.config || Bkper.globalConfig;
    }
    /**
     * Gets the unique identifier of this Book.
     *
     * @returns This Book's unique identifier
     */
    getId() {
        return this.payload.id || '';
    }
    /**
     * Gets the name of this Book.
     *
     * @returns The name of this Book
     */
    getName() {
        return this.payload.name;
    }
    /**
     * Sets the name of the Book.
     *
     * @param name - The name to set
     *
     * @returns This Book, for chaining
     */
    setName(name) {
        this.payload.name = name;
        return this;
    }
    /**
     * Gets the number of fraction digits supported by this Book.
     *
     * @returns The number of fraction digits supported by this Book. Same as getDecimalPlaces
     */
    getFractionDigits() {
        return this.payload.fractionDigits;
    }
    /**
     * Gets the number of decimal places supported by this Book.
     *
     * @returns The number of decimal places supported by this Book. Same as getFractionDigits
     */
    getDecimalPlaces() {
        return this.getFractionDigits();
    }
    /**
     * Sets the number of fraction digits (decimal places) supported by this Book.
     *
     * @param fractionDigits - The number of fraction digits to set (0 to 8)
     *
     * @returns This Book, for chaining
     */
    setFractionDigits(fractionDigits) {
        this.payload.fractionDigits = fractionDigits;
        return this;
    }
    /**
     * Gets the period slice for balances visualization.
     *
     * @returns The period slice for balances visualization
     */
    getPeriod() {
        return this.payload.period;
    }
    /**
     * Sets the period slice for balances visualization.
     *
     * @param period - The period to set
     *
     * @returns This Book, for chaining
     */
    setPeriod(period) {
        this.payload.period = period;
        return this;
    }
    /**
     * Gets the start month when YEAR period is set.
     *
     * @returns The start month when YEAR period is set
     */
    getPeriodStartMonth() {
        return this.payload.periodStartMonth;
    }
    /**
     * Sets the start month when YEAR period is set.
     *
     * @param month - The start month to set
     *
     * @returns This Book, for chaining
     */
    setPeriodStartMonth(month) {
        this.payload.periodStartMonth = month;
        return this;
    }
    /**
     * Gets the transactions pagination page size.
     *
     * @returns The transactions pagination page size
     */
    getPageSize() {
        return this.payload.pageSize;
    }
    /**
     * Sets the transactions pagination page size.
     *
     * @param pageSize - The page size to set
     *
     * @returns This Book, for chaining
     */
    setPageSize(pageSize) {
        this.payload.pageSize = pageSize;
        return this;
    }
    /**
     * Gets the name of the owner of the Book.
     *
     * @returns The name of the owner of the Book
     */
    getOwnerName() {
        return this.payload.ownerName;
    }
    /**
     * Gets the logo URL of the Book owner's custom domain, if any.
     *
     * @returns The logo URL, or undefined if the owner has no custom domain logo
     */
    getLogoUrl() {
        return this.payload.logoUrl;
    }
    /**
     * Gets the permission for the current user in this Book.
     *
     * @returns The permission for the current user in this Book
     */
    getPermission() {
        return this.payload.permission;
    }
    /**
     * Gets the collection of this Book, if any.
     *
     * @returns The collection of this Book, if any
     */
    getCollection() {
        if (this.payload.collection != null && this.collection == null) {
            this.collection = new Collection(this.payload.collection, this.config);
        }
        return this.collection;
    }
    /**
     * Gets the date pattern of the Book.
     *
     * @returns The date pattern of the Book. Current: dd/MM/yyyy | MM/dd/yyyy | yyyy/MM/dd
     */
    getDatePattern() {
        return this.payload.datePattern;
    }
    /**
     * Sets the date pattern of the Book. Current: dd/MM/yyyy | MM/dd/yyyy | yyyy/MM/dd
     *
     * @param datePattern - The date pattern to set
     *
     * @returns This Book, for chaining
     */
    setDatePattern(datePattern) {
        this.payload.datePattern = datePattern;
        return this;
    }
    /**
     * Gets the lock date of the Book in ISO format yyyy-MM-dd.
     *
     * @returns The lock date of the Book in ISO format yyyy-MM-dd
     */
    getLockDate() {
        return this.payload.lockDate;
    }
    /**
     * Sets the lock date of the Book in ISO format yyyy-MM-dd.
     *
     * @param lockDate - The lock date to set in ISO format yyyy-MM-dd
     *
     * @returns This Book, for chaining
     */
    setLockDate(lockDate) {
        if (lockDate == null) {
            lockDate = '1900-00-00';
        }
        this.payload.lockDate = lockDate;
        return this;
    }
    /**
     * Gets the closing date of the Book in ISO format yyyy-MM-dd.
     *
     * @returns The closing date of the Book in ISO format yyyy-MM-dd
     */
    getClosingDate() {
        return this.payload.closingDate;
    }
    /**
     * Sets the closing date of the Book in ISO format yyyy-MM-dd.
     *
     * @param closingDate - The closing date to set in ISO format yyyy-MM-dd
     *
     * @returns This Book, for chaining
     */
    setClosingDate(closingDate) {
        if (closingDate == null) {
            closingDate = '1900-00-00';
        }
        this.payload.closingDate = closingDate;
        return this;
    }
    /**
     * Gets the decimal separator of the Book.
     *
     * @returns The decimal separator of the Book
     */
    getDecimalSeparator() {
        return this.payload.decimalSeparator;
    }
    /**
     * Sets the decimal separator of the Book
     *
     * @param decimalSeparator - The decimal separator to set
     *
     * @returns This Book, for chaining
     */
    setDecimalSeparator(decimalSeparator) {
        this.payload.decimalSeparator = decimalSeparator;
        return this;
    }
    /**
     * Gets the time zone of the Book.
     *
     * @returns The time zone of the Book
     */
    getTimeZone() {
        return this.payload.timeZone;
    }
    /**
     * Sets the time zone of the Book.
     *
     * @param timeZone - The time zone to set
     *
     * @returns This Book, for chaining
     */
    setTimeZone(timeZone) {
        this.payload.timeZone = timeZone;
        return this;
    }
    /**
     * Gets the time zone offset of the book, in minutes.
     *
     * @returns The time zone offset of the book, in minutes
     */
    getTimeZoneOffset() {
        return this.payload.timeZoneOffset;
    }
    /**
     * Gets the auto post status of the Book.
     *
     * @returns The auto post status of the Book
     */
    getAutoPost() {
        return this.payload.autoPost;
    }
    /**
     * Sets the auto post status of the Book.
     *
     * @param autoPost - The auto post status to set
     *
     * @returns This Book, for chaining
     */
    setAutoPost(autoPost) {
        this.payload.autoPost = autoPost;
        return this;
    }
    /**
     * Gets the last update date of the book, in milliseconds.
     *
     * @returns The last update date of the book, in milliseconds
     */
    getLastUpdateMs() {
        return this.payload.lastUpdateMs ? +this.payload.lastUpdateMs : undefined;
    }
    /**
     * Gets the total number of posted transactions.
     *
     * @returns The total number of posted transactions
     */
    getTotalTransactions() {
        return this.payload.totalTransactions ? +this.payload.totalTransactions : 0;
    }
    /**
     * Gets the total number of posted transactions on current month.
     *
     * @returns The total number of posted transactions on current month
     */
    getTotalTransactionsCurrentMonth() {
        return this.payload.totalTransactionsCurrentMonth
            ? +this.payload.totalTransactionsCurrentMonth
            : 0;
    }
    /**
     * Gets the total number of posted transactions on current year.
     *
     * @returns The total number of posted transactions on current year
     */
    getTotalTransactionsCurrentYear() {
        return this.payload.totalTransactionsCurrentYear
            ? +this.payload.totalTransactionsCurrentYear
            : 0;
    }
    /**
     * Gets the visibility of the book.
     *
     * @returns The visibility of the book
     */
    getVisibility() {
        return this.payload.visibility;
    }
    /**
     * Sets the visibility of the book.
     *
     * @param visibility - The visibility to set
     *
     * @returns This Book, for chaining
     */
    setVisibility(visibility) {
        this.payload.visibility = visibility;
        return this;
    }
    /**
     * Formats a date according to date pattern of the Book.
     *
     * @param date - The date to format as string.
     * @param timeZone - The output timezone of the result. Default to script's timeZone
     *
     * @returns The formatted date
     */
    formatDate(date, timeZone) {
        if (timeZone == null || timeZone.trim() == '') {
            timeZone = this.getTimeZone();
        }
        return Utils.formatDate(date, this.getDatePattern(), timeZone);
    }
    /**
     * Parse a date string according to date pattern and timezone of the Book. Also parse ISO yyyy-mm-dd format.
     *
     * @param date - The date string to parse
     *
     * @returns The parsed Date object
     */
    parseDate(date) {
        return Utils.parseDate(date, this.getDatePattern(), this.getTimeZone());
    }
    /**
     * Formats a value according to [[DecimalSeparator]] and fraction digits of the Book.
     *
     * @param value - The value to be formatted.
     *
     * @returns The formatted value
     */
    formatValue(value) {
        if (!value) {
            return '';
        }
        return Utils.formatValue(value, this.getDecimalSeparator(), this.getFractionDigits());
    }
    /**
     * Parse a value string according to [[DecimalSeparator]] and fraction digits of the Book.
     *
     * @param value - The value string to parse
     *
     * @returns The parsed Amount or undefined if parsing fails
     */
    parseValue(value) {
        return Utils.parseValue(value, this.getDecimalSeparator());
    }
    /**
     * Rounds a value according to the number of fraction digits of the Book.
     *
     * @param value - The value to be rounded
     *
     * @returns The rounded value
     */
    round(value) {
        return Utils.round(value, this.getFractionDigits());
    }
    /**
     * Batch create [[Transactions]] on the Book.
     *
     * @param transactions - The transactions to be created
     *
     * @returns The created Transactions
     */
    batchCreateTransactions(transactions) {
        return __awaiter(this, void 0, void 0, function* () {
            let transactionPayloads = [];
            transactions.forEach(tx => transactionPayloads.push(tx.json()));
            transactionPayloads = yield TransactionService.createTransactionsBatch(this.getId(), transactionPayloads, this.getConfig());
            transactions = transactionPayloads.map(tx => new Transaction(this, tx));
            return transactions;
        });
    }
    /**
     * Batch post [[Transactions]] on the Book.
     *
     * @param transactions - The transactions to be posted
     */
    batchPostTransactions(transactions) {
        return __awaiter(this, void 0, void 0, function* () {
            let transactionPayloads = [];
            transactions.forEach(tx => transactionPayloads.push(tx.json()));
            yield TransactionService.postTransactionsBatch(this.getId(), transactionPayloads, this.getConfig());
        });
    }
    /**
     * Batch update [[Transactions]] on the Book.
     *
     * @param transactions - The transactions to be updated
     *
     * @param updateChecked - True to also update checked transactions
     *
     * @returns The updated draft Transactions
     */
    batchUpdateTransactions(transactions, updateChecked) {
        return __awaiter(this, void 0, void 0, function* () {
            let transactionPayloads = [];
            transactions.forEach(tx => transactionPayloads.push(tx.json()));
            transactionPayloads = yield TransactionService.updateTransactionsBatch(this.getId(), transactionPayloads, updateChecked, this.getConfig());
            transactions = transactionPayloads.map(tx => new Transaction(this, tx));
            return transactions;
        });
    }
    /**
     * Batch check [[Transactions]] on the Book.
     *
     * @param transactions - The transactions to be checked
     */
    batchCheckTransactions(transactions) {
        return __awaiter(this, void 0, void 0, function* () {
            let transactionPayloads = [];
            transactions.forEach(tx => transactionPayloads.push(tx.json()));
            yield TransactionService.checkTransactionsBatch(this.getId(), transactionPayloads, this.getConfig());
        });
    }
    /**
     * Batch uncheck [[Transactions]] on the Book.
     *
     * @param transactions - The transactions to be unchecked
     */
    batchUncheckTransactions(transactions) {
        return __awaiter(this, void 0, void 0, function* () {
            let transactionPayloads = [];
            transactions.forEach(tx => transactionPayloads.push(tx.json()));
            yield TransactionService.uncheckTransactionsBatch(this.getId(), transactionPayloads, this.getConfig());
        });
    }
    /**
     * Batch trash [[Transactions]] on the Book.
     *
     * @param transactions - The transactions to be trashed
     * @param trashChecked - True to also trash checked transactions
     */
    batchTrashTransactions(transactions, trashChecked) {
        return __awaiter(this, void 0, void 0, function* () {
            let transactionPayloads = [];
            transactions.forEach(tx => transactionPayloads.push(tx.json()));
            yield TransactionService.trashTransactionsBatch(this.getId(), transactionPayloads, trashChecked, this.getConfig());
        });
    }
    /**
     * Batch untrash [[Transactions]] on the Book.
     *
     * @param transactions - The transactions to be untrashed
     */
    batchUntrashTransactions(transactions) {
        return __awaiter(this, void 0, void 0, function* () {
            let transactionPayloads = [];
            transactions.forEach(tx => transactionPayloads.push(tx.json()));
            yield TransactionService.untrashTransactionsBatch(this.getId(), transactionPayloads, this.getConfig());
        });
    }
    /**
     * Merge a primary and secondary [[Transaction]]. Submitted primary fields
     * have highest precedence; an id string sends no field overrides.
     *
     * @param primary - Primary Transaction, payload, or id.
     * @param secondary - Secondary Transaction, payload, or id.
     * @returns The merged Transaction
     */
    mergeTransactions(primary, secondary) {
        return __awaiter(this, void 0, void 0, function* () {
            const primaryPayload = typeof primary === 'string'
                ? { id: primary }
                : primary instanceof Transaction
                    ? primary.json()
                    : primary;
            if (primaryPayload.id == null || primaryPayload.id.trim() === '') {
                throw new Error('The primary transaction must provide an id for merge.');
            }
            const secondaryPayload = typeof secondary === 'string'
                ? { id: secondary }
                : secondary instanceof Transaction
                    ? secondary.json()
                    : secondary;
            if (secondaryPayload.id == null || secondaryPayload.id.trim() === '') {
                throw new Error('The secondary transaction must provide an id for merge.');
            }
            const payload = {
                items: [primaryPayload, secondaryPayload],
            };
            let operation = yield TransactionService.mergeTransactions(this.getId(), payload, this.getConfig());
            return new Transaction(this, operation.transaction || {});
        });
    }
    /**
     * Replay [[Events]] on the Book, in batch.
     *
     * @param events - The events to be replayed
     * @param errorOnly - True to only replay events with errors
     */
    batchReplayEvents(events, errorOnly) {
        return __awaiter(this, void 0, void 0, function* () {
            const eventIds = events.map(event => event.getId());
            const eventPayloads = eventIds.map(id => ({ id: id }));
            const eventList = { items: eventPayloads };
            yield EventService.replayEventsBatch(this, eventList, errorOnly, this.getConfig());
        });
    }
    /**
     * Create [[Accounts]] on the Book, in batch.
     *
     * @param accounts - The accounts to be created
     *
     * @returns The created Accounts
     */
    batchCreateAccounts(accounts) {
        return __awaiter(this, void 0, void 0, function* () {
            if (accounts.length > 0) {
                const accountList = {
                    items: yield Promise.all(accounts.map(account => this.normalizeAccountPayloadForBatchCreate_(account))),
                };
                const payloads = yield AccountService.createAccounts(this.getId(), accountList, this.getConfig());
                const createdAccounts = [];
                for (const payload of payloads) {
                    const account = new Account(this, payload);
                    createdAccounts.push(account);
                    this.setAccount(payload);
                }
                this.clearCache();
                return createdAccounts;
            }
            return [];
        });
    }
    /** @internal */
    normalizeAccountPayloadForBatchCreate_(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = account.json();
            if (!payload.groups || payload.groups.length === 0) {
                return payload;
            }
            const normalizedGroups = [];
            for (const groupPayload of payload.groups) {
                const idOrName = groupPayload.id || groupPayload.name;
                if (!idOrName || idOrName.trim() === '') {
                    throw new Error('Account group reference must include id or name');
                }
                const group = yield this.getGroup(idOrName);
                if (!group) {
                    throw new Error(`Group not found: ${idOrName}`);
                }
                normalizedGroups.push(group.json());
            }
            payload.groups = normalizedGroups;
            return payload;
        });
    }
    /**
     * Update [[Accounts]] on the Book, in batch.
     *
     * @param accounts - The accounts to be updated
     *
     * @returns The updated Accounts
     */
    batchUpdateAccounts(accounts) {
        return __awaiter(this, void 0, void 0, function* () {
            if (accounts.length > 0) {
                const accountList = {
                    items: accounts.map(a => a.json()),
                };
                const payloads = yield AccountService.updateAccounts(this.getId(), accountList, this.getConfig());
                const updatedAccounts = [];
                for (const payload of payloads) {
                    const account = new Account(this, payload);
                    updatedAccounts.push(account);
                    this.setAccount(payload);
                }
                this.clearCache();
                return updatedAccounts;
            }
            return [];
        });
    }
    /**
     * Delete [[Accounts]] on the Book, in batch.
     *
     * @param accounts - The accounts to be deleted
     *
     * @returns The deleted Accounts
     */
    batchDeleteAccounts(accounts) {
        return __awaiter(this, void 0, void 0, function* () {
            if (accounts.length > 0) {
                const accountList = {
                    items: accounts.map(a => a.json()),
                };
                const payloads = yield AccountService.deleteAccounts(this.getId(), accountList, this.getConfig());
                const deletedAccounts = [];
                for (const payload of payloads) {
                    const account = new Account(this, payload);
                    deletedAccounts.push(account);
                    this.setAccount(payload, true);
                }
                this.clearCache();
                return deletedAccounts;
            }
            return [];
        });
    }
    /**
     * Create [[Groups]] on the Book, in batch.
     *
     * @param groups - The groups to be created
     *
     * @returns The created Groups
     */
    batchCreateGroups(groups) {
        return __awaiter(this, void 0, void 0, function* () {
            if (groups.length > 0) {
                const groupList = { items: groups.map(g => g.json()) };
                const payloads = yield GroupService.createGroups(this.getId(), groupList, this.getConfig());
                const createdGroups = [];
                for (const payload of payloads) {
                    const group = new Group(this, payload);
                    createdGroups.push(group);
                    this.setGroup(payload);
                }
                this.clearCache();
                return createdGroups;
            }
            return [];
        });
    }
    /**
     * Trigger Balances Audit async process.
     */
    audit() {
        BookService.audit(this.getId(), this.getConfig());
    }
    /**
     * Retrieve installed [[Apps]] for this Book.
     *
     * @returns The retrieved Apps objects
     */
    getApps() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.apps != null) {
                return this.apps;
            }
            const appsPlain = yield BookService.getApps(this.getId(), this.getConfig());
            this.apps = appsPlain.map(a => new App(a, this.config));
            return this.apps;
        });
    }
    /**
     * Gets the existing [[Integrations]] in the Book.
     *
     * @returns The retrieved Integration objects
     */
    getIntegrations() {
        return __awaiter(this, void 0, void 0, function* () {
            const integrationsPlain = yield IntegrationService.listIntegrations(this.getId(), this.getConfig());
            const integrations = integrationsPlain.map(i => new Integration(i, this.config));
            return integrations;
        });
    }
    /**
     * Creates a new [[Integration]] in the Book.
     *
     * @param integration - The [[Integration]] object or wrapped plain json
     *
     * @returns The created [[Integration]] object
     */
    createIntegration(integration) {
        return __awaiter(this, void 0, void 0, function* () {
            if (integration instanceof Integration) {
                integration = yield IntegrationService.createIntegration(this.getId(), integration.json(), this.getConfig());
            }
            else {
                integration = yield IntegrationService.createIntegration(this.getId(), integration, this.getConfig());
            }
            return new Integration(integration, this.config);
        });
    }
    /**
     * Updates an existing [[Integration]] in the Book.
     *
     * @param integration - The [[Integration]] wrapped plain json
     *
     * @returns The updated [[Integration]] object
     */
    updateIntegration(integration) {
        return __awaiter(this, void 0, void 0, function* () {
            if (integration instanceof Integration) {
                integration = yield IntegrationService.updateIntegration(this.getId(), integration.json(), this.getConfig());
            }
            else {
                integration = yield IntegrationService.updateIntegration(this.getId(), integration, this.getConfig());
            }
            return new Integration(integration, this.config);
        });
    }
    /**
     * Gets an [[Account]] object by id or name.
     *
     * Results are cached to avoid repeated server calls. Account-group relationships
     * are included if the full chart was loaded via getAccounts() or when the Book
     * was loaded with includeAccounts=true.
     *
     * @param idOrName - The id or name of the Account
     *
     * @returns The matching Account object
     *
     * @example
     * ```typescript
     * // Get individual account (basic data, cached)
     * const account = await book.getAccount('Bank Account');
     *
     * // For account-group relationships, use one of these approaches:
     * // Option 1: Load book with full data upfront
     * const bookWithAccounts = await Bkper.getBook(bookId, true);
     * const accountWithGroups = await bookWithAccounts.getAccount('Bank Account');
     *
     * // Option 2: Load full chart when needed
     * await book.getAccounts();
     * const accountWithGroups2 = await book.getAccount('Bank Account');
     * ```
     */
    getAccount(idOrName) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!idOrName || idOrName.trim() == '') {
                return undefined;
            }
            let account;
            // Try to get account by id
            if (this.idAccountMap) {
                account = this.idAccountMap.get(idOrName);
            }
            // Try to get account by name
            if (!account && this.nameAccountMap) {
                account = this.nameAccountMap.get(Utils.normalizeName(idOrName));
            }
            // Try to fetch account from server
            if (!account) {
                const accountPayload = yield AccountService.getAccount(this.getId(), idOrName, this.getConfig());
                if (accountPayload) {
                    account = new Account(this, accountPayload);
                    this.updateAccountCache(account);
                }
            }
            return account;
        });
    }
    /** @internal */
    updateGroupCache(group) {
        this.updateGroupIdMap(group);
        this.updateGroupNameMap(group);
    }
    /** @internal */
    updateGroupIdMap(group) {
        this.idGroupMap = this.idGroupMap || new Map();
        const id = group.getId();
        if (id) {
            this.idGroupMap.set(id, group);
        }
    }
    /** @internal */
    updateGroupNameMap(group) {
        this.nameGroupMap = this.nameGroupMap || new Map();
        const normalizedName = group.getNormalizedName();
        if (normalizedName) {
            this.nameGroupMap.set(normalizedName, group);
        }
    }
    /** @internal */
    updateAccountCache(account) {
        this.updateAccountIdMap(account);
        this.updateAccountNameMap(account);
    }
    /** @internal */
    updateAccountIdMap(account) {
        this.idAccountMap = this.idAccountMap || new Map();
        const id = account.getId();
        if (id) {
            this.idAccountMap.set(id, account);
        }
    }
    /** @internal */
    updateAccountNameMap(account) {
        this.nameAccountMap = this.nameAccountMap || new Map();
        const normalizedName = account.getNormalizedName();
        if (normalizedName) {
            this.nameAccountMap.set(normalizedName, account);
        }
    }
    /** @internal */
    getMostRecentLockDate_() {
        const closingDate = this.getClosingDate();
        const lockDate = this.getLockDate();
        if (!closingDate && !lockDate) {
            return null;
        }
        if (!closingDate && lockDate) {
            return lockDate;
        }
        if (closingDate && !lockDate) {
            return closingDate;
        }
        if (Utils.getIsoDateValue(closingDate) > Utils.getIsoDateValue(lockDate)) {
            return closingDate;
        }
        else {
            return lockDate;
        }
    }
    /**
     * Gets a [[Group]] object by id or name.
     *
     * Results are cached to avoid repeated server calls. Parent/child relationships
     * are included if all groups were loaded via getGroups() or when the Book was
     * loaded with includeGroups=true.
     *
     * @param idOrName - The id or name of the Group
     *
     * @returns The matching Group object
     *
     * @example
     * ```typescript
     * // Get individual group (basic data, cached)
     * const group = await book.getGroup('Assets');
     *
     * // For parent/child relationships, use one of these approaches:
     * // Option 1: Load book with full hierarchy upfront
     * const bookWithGroups = await Bkper.getBook(bookId, false, true);
     * const groupWithTree = await bookWithGroups.getGroup('Assets');
     *
     * // Option 2: Load full hierarchy when needed
     * await book.getGroups();
     * const groupWithTree2 = await book.getGroup('Assets');
     * console.log(groupWithTree2.getParent(), groupWithTree2.getChildren());
     * ```
     */
    getGroup(idOrName) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!idOrName || idOrName.trim() == '') {
                return undefined;
            }
            let group;
            // Try to get group by id
            if (this.idGroupMap) {
                group = this.idGroupMap.get(idOrName);
            }
            // Try to get group by name
            if (!group && this.nameGroupMap) {
                group = this.nameGroupMap.get(Utils.normalizeName(idOrName));
            }
            // Try to fetch group from server
            if (!group) {
                const groupPayload = yield GroupService.getGroup(this.getId(), idOrName, this.getConfig());
                if (groupPayload) {
                    group = new Group(this, groupPayload);
                    this.updateGroupCache(group);
                }
            }
            return group;
        });
    }
    /**
     * Gets all [[Groups]] of this Book with complete parent/child hierarchy.
     *
     * Results are cached for performance. Group tree relationships are built
     * during loading. Consider using Bkper.getBook(id, false, true) for
     * upfront loading when you know you'll need all groups.
     *
     * @returns The retrieved [[Group]] objects
     *
     * @example
     * ```typescript
     * // Load all groups with complete hierarchy
     * const groups = await book.getGroups();
     *
     * // Alternative: Load book with groups upfront (more efficient)
     * const bookWithGroups = await Bkper.getBook(bookId, false, true);
     * const groups2 = await bookWithGroups.getGroups(); // Already cached
     * ```
     */
    getGroups() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (this.idGroupMap && this.allGroupsLoaded) {
                return Array.from(((_a = this.idGroupMap) === null || _a === void 0 ? void 0 : _a.values()) || []);
            }
            let groups = yield GroupService.getGroups(this.getId(), this.getConfig());
            this.allGroupsLoaded = this.mapGroups(groups);
            return Array.from(((_b = this.idGroupMap) === null || _b === void 0 ? void 0 : _b.values()) || []);
        });
    }
    /** @internal */
    mapGroups(groups) {
        if (!groups) {
            return false;
        }
        let groupsObj = groups.map(group => new Group(this, group));
        this.idGroupMap = new Map();
        this.nameGroupMap = new Map();
        for (const group of groupsObj) {
            this.updateGroupCache(group);
            group.buildGroupTree(this.idGroupMap);
        }
        for (const group of groupsObj) {
            group.buildGroupTree(this.idGroupMap);
        }
        return true;
    }
    /**
     * Gets all [[Accounts]] of this Book with full account-group relationships.
     *
     * Results are cached for performance. Groups are automatically loaded first
     * to ensure proper linking. Consider using Bkper.getBook(id, true) for
     * upfront loading when you know you'll need all accounts.
     *
     * @returns The retrieved [[Account]] objects
     *
     * @example
     * ```typescript
     * // Load all accounts with complete relationships
     * const accounts = await book.getAccounts();
     *
     * // Alternative: Load book with accounts upfront (more efficient)
     * const bookWithAccounts = await Bkper.getBook(bookId, true);
     * const accounts2 = await bookWithAccounts.getAccounts(); // Already cached
     * ```
     */
    getAccounts() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (this.idAccountMap && this.allAccountsLoaded) {
                return Array.from(((_a = this.idAccountMap) === null || _a === void 0 ? void 0 : _a.values()) || []);
            }
            //Ensure groups are loaded
            yield this.getGroups();
            let accounts = yield AccountService.getAccounts(this.getId(), this.getConfig());
            this.allAccountsLoaded = this.mapAccounts(accounts);
            return Array.from(((_b = this.idAccountMap) === null || _b === void 0 ? void 0 : _b.values()) || []);
        });
    }
    /** @internal */
    mapAccounts(accounts) {
        if (!accounts) {
            return false;
        }
        let accountsObj = accounts.map(account => new Account(this, account));
        this.idAccountMap = new Map();
        this.nameAccountMap = new Map();
        for (const account of accountsObj) {
            this.updateAccountCache(account);
            this.linkAccountsAndGroups(account);
        }
        this.ensureGroupsAccountMapsLoaded();
        return true;
    }
    /** @internal */
    linkAccountsAndGroups(account) {
        var _a;
        const groupPayloads = account.json().groups || [];
        for (const groupPayload of groupPayloads) {
            const group = (_a = this.idGroupMap) === null || _a === void 0 ? void 0 : _a.get(groupPayload.id || '');
            if (group != null) {
                group.addAccount(account);
            }
        }
    }
    /**
     * Ensures every group has a cached `.accounts` Map after accounts are loaded.
     *
     * `linkAccountsAndGroups()` populates `.accounts` on groups that have accounts
     * linked to them. Groups with zero accounts keep `.accounts = undefined`. This
     * method fills that gap by initializing an empty Map on those groups.
     *
     * Without this, `Group.getAccounts()` sees `undefined`, skips the cache check,
     * and falls through to a network request per group — causing hundreds of
     * redundant `/groups/{id}/accounts` calls on books with many groups.
     *
     * IMPORTANT: This method must NOT be guarded by `allGroupsLoaded` or any
     * similar flag. It runs inside `mapAccounts()`, which is always called after
     * `mapGroups()` sets `allGroupsLoaded = true`. A guard like
     * `if (!this.allGroupsLoaded)` would make this method dead code, silently
     * reintroducing the redundant network requests.
     *
     * @internal
     */
    ensureGroupsAccountMapsLoaded() {
        if (this.idGroupMap) {
            for (const group of this.idGroupMap.values()) {
                if (group.accounts == null) {
                    group.accounts = new Map();
                }
            }
        }
    }
    /** @internal */
    clearCache() {
        this.clearGroupCache();
        this.clearAccountCache();
    }
    /** @internal */
    clearGroupCache() {
        this.allGroupsLoaded = false;
        this.idGroupMap = undefined;
        this.nameGroupMap = undefined;
    }
    /** @internal */
    clearAccountCache() {
        this.allAccountsLoaded = false;
        this.idAccountMap = undefined;
        this.nameAccountMap = undefined;
    }
    /** @internal */
    clearCollaboratorCache() {
        this.collaborators = undefined;
    }
    /** @internal */
    setAccount(account, remove) {
        const accountPayloads = this.payload.accounts || [];
        if (remove) {
            this.payload.accounts = accountPayloads.filter(a => a.id !== account.id);
        }
        else {
            const existingAccount = accountPayloads.find(a => a.id === account.id);
            if (existingAccount) {
                this.payload.accounts = accountPayloads.map(a => a.id === account.id ? account : a);
            }
            else {
                this.payload.accounts = [...accountPayloads, account];
            }
        }
    }
    /** @internal */
    setGroup(group, remove) {
        const groupPayloads = this.payload.groups || [];
        if (remove) {
            this.payload.groups = groupPayloads.filter(g => g.id !== group.id);
        }
        else {
            const existingGroup = groupPayloads.find(g => g.id === group.id);
            if (existingGroup) {
                this.payload.groups = groupPayloads.map(g => (g.id === group.id ? group : g));
            }
            else {
                this.payload.groups = [...groupPayloads, group];
            }
        }
    }
    /**
     * Lists transactions in the Book based on the provided query, limit, and cursor, for pagination.
     *
     * @param query - The query string to filter transactions
     * @param limit - The maximum number of transactions to return. Default to 100, max to 1000
     * @param cursor - The cursor for pagination
     *
     * @returns A [[TransactionList]] object containing the list of transactions
     */
    listTransactions(query, limit, cursor) {
        return __awaiter(this, void 0, void 0, function* () {
            const transactionsList = yield TransactionService.listTransactions(this.getId(), query, limit, cursor, this.getConfig());
            return new TransactionList(this, transactionsList);
        });
    }
    /**
     * Lists files in the Book, for pagination.
     *
     * @param limit - The maximum number of files to return. Default to 100
     * @param cursor - The cursor for pagination
     *
     * @returns A [[FileList]] object containing the list of files
     */
    listFiles(limit, cursor) {
        return __awaiter(this, void 0, void 0, function* () {
            const fileList = yield FileService.listFiles(this.getId(), limit, cursor, this.getConfig());
            return new FileList(this, fileList);
        });
    }
    /**
     * Retrieve the number of transactions based on a query.
     *
     * @param query - The query string
     *
     * @returns The number of matching transactions
     */
    countTransactions(query) {
        return __awaiter(this, void 0, void 0, function* () {
            const count = yield TransactionService.countTransactions(this.getId(), query, this.getConfig());
            // int64 totals arrive as JSON strings
            return count.total != null ? +count.total : undefined;
        });
    }
    listEvents(optionsOrAfterDate, beforeDate, onError, resourceId, limit, cursor) {
        return __awaiter(this, void 0, void 0, function* () {
            const isOptions = typeof optionsOrAfterDate === 'object' && optionsOrAfterDate !== null;
            if (isOptions) {
                return this.listEventsWithOptions(optionsOrAfterDate);
            }
            const options = {
                afterDate: optionsOrAfterDate !== null && optionsOrAfterDate !== void 0 ? optionsOrAfterDate : undefined,
                beforeDate: beforeDate !== null && beforeDate !== void 0 ? beforeDate : undefined,
                resourceId: resourceId !== null && resourceId !== void 0 ? resourceId : undefined,
                onError: onError !== null && onError !== void 0 ? onError : undefined,
                type: undefined,
                limit: limit !== null && limit !== void 0 ? limit : 50,
                cursor,
            };
            return this.listEventsWithOptions(options);
        });
    }
    /** @internal */
    listEventsWithOptions(options) {
        return __awaiter(this, void 0, void 0, function* () {
            const eventList = yield EventService.listEvents(this, options.afterDate, options.beforeDate, options.onError, options.resourceId, options.type, options.limit, options.cursor, this.getConfig());
            return new EventList(this, eventList);
        });
    }
    /**
     * Retrieve a transaction by id.
     *
     * @param id - The transaction ID
     *
     * @returns The [[Transaction]] object
     */
    getTransaction(id) {
        return __awaiter(this, void 0, void 0, function* () {
            let wrapped = yield TransactionService.getTransaction(this.getId(), id, this.getConfig());
            if (!wrapped) {
                return undefined;
            }
            let transaction = new Transaction(this, wrapped);
            return transaction;
        });
    }
    /**
     * Retrieve complete transactions by id.
     *
     * Requests are sent sequentially in batches of up to 200 IDs.
     *
     * @param ids - The transaction IDs
     *
     * @returns The matching Transactions in input order
     */
    getTransactionsByIds(ids) {
        return __awaiter(this, void 0, void 0, function* () {
            if (ids == null) {
                throw new Error('Transaction IDs are required.');
            }
            const distinctIds = new Set();
            const normalizedIds = [];
            for (const id of ids) {
                if (id == null || typeof id !== 'string' || id.trim() === '') {
                    throw new Error('Transaction IDs must be non-blank strings.');
                }
                const normalizedId = id.trim();
                if (distinctIds.has(normalizedId)) {
                    throw new Error(`Duplicate transaction ID: ${normalizedId}`);
                }
                distinctIds.add(normalizedId);
                normalizedIds.push(normalizedId);
            }
            const transactions = [];
            for (let index = 0; index < normalizedIds.length; index += 200) {
                const payloads = yield TransactionService.getTransactionsByIds(this.getId(), normalizedIds.slice(index, index + 200), this.getConfig());
                transactions.push(...payloads.map(payload => new Transaction(this, payload)));
            }
            return transactions;
        });
    }
    /**
     * Retrieve a file by id.
     *
     * @param id - The file ID
     *
     * @returns The [[File]] object
     */
    getFile(id) {
        return __awaiter(this, void 0, void 0, function* () {
            let wrapped = yield FileService.getFile(this.getId(), id, this.getConfig());
            if (!wrapped) {
                return undefined;
            }
            let file = new File(this, wrapped);
            return file;
        });
    }
    /**
     * Performs create new Book.
     *
     * @returns The created Book object
     */
    create() {
        return __awaiter(this, void 0, void 0, function* () {
            this.payload = yield BookService.createBook(this.payload, this.getConfig());
            return this;
        });
    }
    /**
     * Creates a copy of this Book
     *
     * @param name - The name for the copied book
     * @param copyTransactions - True to copy transactions from the source book (user must be the Book owner)
     * @param fromDate - Start date to consider if copying transactions (numeric value in YYYYMMDD format)
     *
     * @returns The copied Book object
     */
    copy(name, copyTransactions, fromDate) {
        return __awaiter(this, void 0, void 0, function* () {
            const copiedBookPayload = yield BookService.copyBook(this.getId(), name, copyTransactions, fromDate, this.getConfig());
            return new Book(copiedBookPayload, this.config);
        });
    }
    /**
     * Perform update Book, applying pending changes.
     *
     * @returns The updated Book object
     */
    update() {
        return __awaiter(this, void 0, void 0, function* () {
            this.payload = yield BookService.updateBook(this.getId(), this.payload, this.getConfig());
            return this;
        });
    }
    /**
     * Warning!
     *
     * Deletes this Book and all its data (transactions, accounts, groups). Book owner only.
     *
     * @returns This Book after deletion
     */
    remove() {
        return __awaiter(this, void 0, void 0, function* () {
            this.payload = yield BookService.deleteBook(this.getId(), this.getConfig());
            return this;
        });
    }
    /**
     * Create a [[BalancesReport]] based on query.
     *
     * @param query - The balances report query
     *
     * @returns The balances report
     *
     * Example:
     *
     * ```js
     * var book = BkperApp.getBook("agtzfmJrcGVyLWhyZHITCxIGTGVkZ2VyGICAgPXjx7oKDA");
     *
     * var balancesReport = book.getBalancesReport("group:'Equity' after:7/2018 before:8/2018");
     *
     * var accountBalance = balancesReport.getBalancesContainer("Bank Account").getCumulativeBalance();
     * ```
     *
     * @returns The retrieved [[BalancesReport]] object
     */
    getBalancesReport(query) {
        return __awaiter(this, void 0, void 0, function* () {
            const balances = yield BalancesService.getBalances(this.getId(), query, this.getConfig());
            return new BalancesReport(this, balances);
        });
    }
    /**
     * Gets the saved queries from this book.
     *
     * @returns The saved queries from this book
     */
    getSavedQueries() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.queries == null) {
                const queryPayloads = yield QueryService.getSavedQueries(this.getId(), this.getConfig());
                this.queries = queryPayloads.map(payload => new Query(this, payload));
            }
            return this.queries;
        });
    }
    /**
     * Resolves an access request for this Book.
     *
     * Resolving the request does not grant access. Call Collaborator.create
     * on the returned Collaborator to grant access to the Book.
     *
     * @param accessRequestId - The access request id
     * @returns The matching [[Collaborator]] object
     */
    resolveAccessRequest(accessRequestId) {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = yield CollaboratorService.resolveBookAccessRequest(this.getId(), accessRequestId, this.getConfig());
            return new Collaborator(this, payload);
        });
    }
    /**
     * Gets all collaborators of this Book.
     *
     * @returns Array of Collaborator objects
     */
    getCollaborators() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.collaborators) {
                const collaboratorPayloads = yield CollaboratorService.listCollaborators(this.getId(), this.getConfig());
                this.collaborators = collaboratorPayloads.map(payload => new Collaborator(this, payload));
            }
            return this.collaborators;
        });
    }
    /**
     * Gets the Backlog of this Book.
     *
     * @returns The Backlog object
     */
    getBacklog() {
        return __awaiter(this, void 0, void 0, function* () {
            const backlogPayload = yield EventService.getBacklog(this, this.getConfig());
            return new Backlog(backlogPayload, this.config);
        });
    }
    /**
     * Create a {@link AccountsDataTableBuilder}, to build two dimensional Array representations of {@link Account} dataset.
     *
     * @param accounts - Optional array of accounts. If not provided, all accounts will be fetched.
     *
     * @returns Accounts data table builder.
     */
    createAccountsDataTable(accounts) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!accounts) {
                accounts = yield this.getAccounts();
            }
            return new AccountsDataTableBuilder(accounts);
        });
    }
    /**
     * Create a {@link GroupsDataTableBuilder}, to build two dimensional Array representations of {@link Group} dataset.
     *
     * @param groups - Optional array of groups. If not provided, all groups will be fetched.
     *
     * @returns Groups data table builder.
     */
    createGroupsDataTable(groups) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!groups) {
                groups = yield this.getGroups();
            }
            return new GroupsDataTableBuilder(groups);
        });
    }
    /**
     * Create a {@link TransactionsDataTableBuilder}, to build two dimensional Array representations of {@link Transaction} dataset.
     *
     * @param transactions - Array of transactions to include in the table.
     * @param account - Optional account for balance column display.
     *
     * @returns Transactions data table builder.
     */
    createTransactionsDataTable(transactions, account) {
        return new TransactionsDataTableBuilder(this, transactions, account);
    }
}
//# sourceMappingURL=Book.js.map